import { DayPicker as RawDayPicker } from 'react-day-picker';
import styled from 'styled-components';
import { setAlpha } from '../../styles/utils';

export const DayPicker = styled(RawDayPicker)`
  padding: ${({ theme }) => theme.spacing12}px;
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  width: 100%;

  /* Variables declaration */
  &.rdp-root {
    --rdp-accent-color: ${({ theme }) =>
      theme.colorTextImportant}; /* The accent color used for selected days and UI elements. */
    --rdp-accent-background-color: ${({ theme }) =>
      theme.backgroundPriorityButtonHover}; /* The accent background color used for selected days and UI elements. */

    --rdp-day-height: 20px; /* The height of the day cells. */
    --rdp-day-width: 26px; /* The width of the day cells. */

    --rdp-day_button-border-radius: ${({ theme }) =>
      theme.borderRadiusDefault}px; /* The border radius of the day cells. */
    --rdp-day_button-border: 2px solid transparent; /* The border of the day cells. */
    --rdp-day_button-height: 20px; /* The height of the day cells. */
    --rdp-day_button-width: 26px; /* The width of the day cells. */

    --rdp-selected-border: 2px solid var(--rdp-accent-color); /* The border of the selected days. */
    --rdp-disabled-opacity: 0.5; /* The opacity of the disabled days. */
    --rdp-outside-opacity: 0.75; /* The opacity of the days outside the current month. */
    --rdp-today-color: var(--rdp-accent-color); /* The color of the today's date. */

    --rdp-dropdown-gap: 0.5rem; /* The gap between the dropdowns used in the month captons. */

    --rdp-months-gap: 1.5rem; /* The gap between the months in the multi-month view. */

    --rdp-nav_button-disabled-opacity: 0.5; /* The opacity of the disabled navigation buttons. */
    --rdp-nav_button-height: 1.25rem; /* The height of the navigation buttons. */
    --rdp-nav_button-width: 1.25rem; /* The width of the navigation buttons. */
    --rdp-nav-height: auto; /* The height of the navigation bar. */

    --rdp-range_middle-background-color: ${({ theme }) =>
      setAlpha(0.1, theme.colors.gray['100'])}; /* The color of the background for days in the middle of a range. */
    --rdp-range_middle-color: inherit; /* The color of the range text. */

    --rdp-range_start-color: ${({ theme }) => theme.colorTextImportant}; /* The color of the range text. */
    --rdp-range_start-background: ${({ theme }) =>
      theme.backgroundDefaultButton}; /* Used for the background of the start of the selected range. */
    --rdp-range_start-date-background-color: ${({ theme }) => theme.backgroundPriorityButton};

    --rdp-range_end-color: ${({ theme }) => theme.colorTextImportant}; /* The color of the range text. */
    --rdp-range_end-background: ${({ theme }) =>
      theme.backgroundDefaultButton}; /* Used for the background of the end of the selected range. */
    --rdp-range_end-date-background-color: ${({ theme }) =>
      theme.backgroundPriorityButton}; /* The background color of the date when at the end of the selected range. */

    --rdp-week_number-border-radius: 100%; /* The border radius of the week number. */
    --rdp-week_number-border: 2px solid transparent; /* The border of the week number. */

    --rdp-week_number-height: var(--rdp-day-height); /* The height of the week number cells. */
    --rdp-week_number-opacity: 0.75; /* The opacity of the week number. */
    --rdp-week_number-width: var(--rdp-day-width); /* The width of the week number cells. */
    --rdp-weeknumber-text-align: center; /* The text alignment of the weekday cells. */

    --rdp-weekday-opacity: 1; /* The opacity of the weekday. */
    --rdp-weekday-padding: 0.25rem 0rem; /* The padding of the weekday. */
    --rdp-weekday-text-align: center; /* The text alignment of the weekday cells. */

    --rdp-gradient-direction: 90deg;

    --rdp-animation_duration: 0.3s;
    --rdp-animation_timing: cubic-bezier(0.4, 0, 0.2, 1);
  }

  .rdp-root[dir='rtl'] {
    --rdp-gradient-direction: -90deg;
  }

  .rdp-root[data-broadcast-calendar='true'] {
    --rdp-outside-opacity: unset;
  }

  /* Root of the component. */
  .rdp-root {
    position: relative; /* Required to position the navigation toolbar. */
    box-sizing: border-box;
  }

  .rdp-root * {
    box-sizing: border-box;
  }

  .rdp-day {
    width: var(--rdp-day-width);
    height: var(--rdp-day-height);
    text-align: center;
  }

  .rdp-day_button {
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    font: inherit;
    font-size: ${({ theme }) => theme.fontSizeTiny}rem;
    color: ${({ theme }) => theme.colorTextDefaultButton};
    justify-content: center;
    align-items: center;
    display: flex;
    background: ${({ theme }) => theme.backgroundDefaultButton};

    width: var(--rdp-day_button-width);
    height: var(--rdp-day_button-height);
    border: var(--rdp-day_button-border);
    border-radius: var(--rdp-day_button-border-radius);
  }

  .rdp-day_button:disabled {
    cursor: revert;
  }

  .rdp-caption_label {
    z-index: 1;

    position: relative;
    display: inline-flex;
    align-items: center;

    white-space: nowrap;
    border: 0;
  }

  .rdp-dropdown:focus-visible ~ .rdp-caption_label {
    outline: 5px auto Highlight;
    /* biome-ignore lint/suspicious/noDuplicateProperties: backward compatibility */
    outline: 5px auto -webkit-focus-ring-color;
  }

  .rdp-button_next,
  .rdp-button_previous {
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    font: inherit;
    color: inherit;
    -moz-appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    appearance: none;
    background: ${({ theme }) => theme.backgroundDefaultButton};
    color: ${({ theme }) => theme.colorTextDefaultButton};
    border-radius: ${({ theme }) => theme.borderRadiusDefault}px;

    width: var(--rdp-nav_button-width);
    height: var(--rdp-nav_button-height);

    svg {
      fill: currentColor;
      height: 1rem;
    }

    :hover {
      background: ${({ theme }) => theme.backgroundDefaultButtonHover};
      color: ${({ theme }) => theme.colorTextDefaultButtonHover};

      svg {
        fill: ${({ theme }) => theme.colorTextDefaultButtonHover};
      }
    }
  }

  .rdp-button_next:disabled,
  .rdp-button_next[aria-disabled='true'],
  .rdp-button_previous:disabled,
  .rdp-button_previous[aria-disabled='true'] {
    cursor: revert;

    opacity: var(--rdp-nav_button-disabled-opacity);
  }

  .rdp-chevron {
    display: inline-block;
    fill: var(--rdp-accent-color);
  }

  .rdp-root[dir='rtl'] .rdp-nav .rdp-chevron {
    transform: rotate(180deg);
    transform-origin: 50%;
  }

  .rdp-dropdowns {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--rdp-dropdown-gap);
  }
  .rdp-dropdown {
    z-index: 2;

    /* Reset */
    opacity: 0;
    appearance: none;
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    cursor: inherit;
    border: none;
    line-height: inherit;
  }

  .rdp-dropdown_root {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .rdp-dropdown_root[data-disabled='true'] .rdp-chevron {
    opacity: var(--rdp-disabled-opacity);
  }

  .rdp-month_caption {
    display: flex;
    align-content: center;
    height: var(--rdp-nav-height);
    font-weight: 500;
    color: ${({ theme }) => theme.colorTextImportant};
    padding: 0 ${({ theme }) => theme.spacing4}px;
    margin-bottom: ${({ theme }) => theme.spacing4}px;
  }

  .rdp-root[data-nav-layout='around'] .rdp-month,
  .rdp-root[data-nav-layout='after'] .rdp-month {
    position: relative;
  }

  .rdp-root[data-nav-layout='around'] .rdp-month_caption {
    justify-content: center;
    margin-inline-start: var(--rdp-nav_button-width);
    margin-inline-end: var(--rdp-nav_button-width);
    position: relative;
  }

  .rdp-root[data-nav-layout='around'] .rdp-button_previous {
    position: absolute;
    inset-inline-start: 0;
    top: 0;
    height: var(--rdp-nav-height);
    display: inline-flex;
  }

  .rdp-root[data-nav-layout='around'] .rdp-button_next {
    position: absolute;
    inset-inline-end: 0;
    top: 0;
    height: var(--rdp-nav-height);
    display: inline-flex;
    justify-content: center;
  }

  .rdp-months {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: var(--rdp-months-gap);
    /* max-width: fit-content; */
  }

  .rdp-month_grid {
    border-collapse: collapse;
  }

  .rdp-nav {
    position: absolute;
    inset-block-start: 0;
    inset-inline-end: 0;

    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing4}px;

    height: var(--rdp-nav-height);
  }

  .rdp-weekday {
    opacity: var(--rdp-weekday-opacity);
    padding: var(--rdp-weekday-padding);
    font-weight: 500;
    font-size: ${({ theme }) => theme.fontSizeTiny}rem;
    color: ${({ theme }) => theme.colorTextMuted};
    text-align: var(--rdp-weekday-text-align);
    text-transform: uppercase;
  }

  .rdp-week_number {
    opacity: var(--rdp-week_number-opacity);
    font-weight: 400;
    font-size: small;
    height: var(--rdp-week_number-height);
    width: var(--rdp-week_number-width);
    border: var(--rdp-week_number-border);
    border-radius: var(--rdp-week_number-border-radius);
    text-align: var(--rdp-weeknumber-text-align);
  }

  /* DAY MODIFIERS */
  .rdp-today:not(.rdp-outside) {
    font-weight: bold;
    color: var(--rdp-today-color);
  }

  .rdp-selected {
    font-weight: bold;
    color: ${({ theme }) => theme.colorTextPriorityButton};
    background: ${({ theme }) => theme.backgroundMutedButton};
  }

  .rdp-outside {
    opacity: var(--rdp-outside-opacity);
  }

  .rdp-disabled {
    opacity: var(--rdp-disabled-opacity);
  }

  .rdp-hidden {
    visibility: hidden;
    color: var(--rdp-range_start-color);
  }

  .rdp-range_start {
    background: var(--rdp-range_start-background);
    color: var(--rdp-range_end-color);
    border-radius: ${({ theme }) => `${theme.borderRadiusDefault}px 0 0 ${theme.borderRadiusDefault}px`};
  }

  .rdp-range_start .rdp-day_button {
    background-color: var(--rdp-range_start-date-background-color);
    color: var(--rdp-range_start-color);
  }

  .rdp-range_middle {
    background-color: var(--rdp-range_middle-background-color);
  }

  .rdp-range_middle .rdp-day_button {
    border: unset;
    color: var(--rdp-range_middle-color);
    background-color: ${({ theme }) => theme.backgroundPriorityButton};
  }

  .rdp-range_end {
    background: var(--rdp-range_end-background);
    color: var(--rdp-range_end-color);
    border-radius: ${({ theme }) => `0 ${theme.borderRadiusDefault}px ${theme.borderRadiusDefault}px 0`};
  }

  .rdp-range_end .rdp-day_button {
    color: var(--rdp-range_start-color);
    background-color: var(--rdp-range_end-date-background-color);
  }

  .rdp-range_start.rdp-range_end {
    background: revert;
  }

  .rdp-focusable {
    cursor: pointer;
  }

  @keyframes rdp-slide_in_left {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes rdp-slide_in_right {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes rdp-slide_out_left {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  @keyframes rdp-slide_out_right {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .rdp-weeks_before_enter {
    animation: rdp-slide_in_left var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-weeks_before_exit {
    animation: rdp-slide_out_left var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-weeks_after_enter {
    animation: rdp-slide_in_right var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-weeks_after_exit {
    animation: rdp-slide_out_right var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-root[dir='rtl'] .rdp-weeks_after_enter {
    animation: rdp-slide_in_left var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-root[dir='rtl'] .rdp-weeks_before_exit {
    animation: rdp-slide_out_right var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-root[dir='rtl'] .rdp-weeks_before_enter {
    animation: rdp-slide_in_right var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-root[dir='rtl'] .rdp-weeks_after_exit {
    animation: rdp-slide_out_left var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  @keyframes rdp-fade_in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes rdp-fade_out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  .rdp-caption_after_enter {
    animation: rdp-fade_in var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-caption_after_exit {
    animation: rdp-fade_out var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-caption_before_enter {
    animation: rdp-fade_in var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }

  .rdp-caption_before_exit {
    animation: rdp-fade_out var(--rdp-animation_duration) var(--rdp-animation_timing) forwards;
  }
`;
